import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Send, CheckCircle2, Building2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import MobileModal from "@/components/MobileModal";

const SECTORS = [
  "E-commerce", "Negozio retail", "Ristorante / catering", "Studio medico / farmacia",
  "Ufficio / studio professionale", "Artigiano", "Magazzino / logistica",
  "Industria / produzione", "Servizi alla persona", "Altro",
];

const PLAN_OPTIONS = [
  { id: "business_start", label: "Business Start (10 consegne/mese)" },
  { id: "business_pro", label: "Business Pro (30 consegne/mese)" },
  { id: "enterprise", label: "Enterprise (100+ consegne/mese)" },
  { id: "unsure", label: "Non so ancora" },
];

const CONTACT_METHODS = [
  { id: "email", label: "Email" },
  { id: "phone", label: "Telefono" },
  { id: "whatsapp", label: "WhatsApp" },
];

const EMPTY = {
  company_name: "",
  contact_name: "",
  email: "",
  phone: "",
  business_sector: "",
  monthly_deliveries_estimate: "",
  message: "",
  operating_area: "",
  interested_plan: "",
  preferred_contact_method: "",
};

export default function DemoRequestModal({ open, onClose, defaultPlan }) {
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({ ...EMPTY, interested_plan: defaultPlan || "" });
      setSuccess(false);
    }
    // Phase 44.13 — scroll lock gestito centralmente da MobileModal.
  }, [open, defaultPlan]);

  if (!open) return null;

  const set = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const valid = () =>
    form.company_name.trim().length >= 1 &&
    form.contact_name.trim().length >= 1 &&
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim()) &&
    form.phone.trim().length >= 4 &&
    form.business_sector.trim().length >= 1 &&
    String(form.monthly_deliveries_estimate).trim() !== "" &&
    Number(form.monthly_deliveries_estimate) >= 0 &&
    form.message.trim().length >= 1;

  const submit = async (e) => {
    e.preventDefault();
    if (!valid()) {
      toast.error("Compila tutti i campi obbligatori (*)");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        company_name: form.company_name.trim(),
        contact_name: form.contact_name.trim(),
        email: form.email.trim().toLowerCase(),
        phone: form.phone.trim(),
        business_sector: form.business_sector,
        monthly_deliveries_estimate: Number(form.monthly_deliveries_estimate),
        message: form.message.trim(),
        operating_area: form.operating_area.trim() || null,
        interested_plan: form.interested_plan || null,
        preferred_contact_method: form.preferred_contact_method || null,
      };
      await api.post("/business/leads", payload);
      setSuccess(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <MobileModal
      open={open}
      onClose={onClose}
      testid="demo-modal"
      size="xl"
    >
        {!success ? (
          <form onSubmit={submit} className="space-y-0">
            <div className="flex items-start gap-3">
              <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-dapnow-blue text-white">
                <Building2 className="h-6 w-6" />
              </span>
              <div>
                <p className="overline text-dapnow-blue">DapNow Business</p>
                <h2 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tighter mt-1">Richiedi una demo</h2>
                <p className="text-sm text-dapnow-muted mt-1">Compila il form, ti contattiamo entro 24h per valutare insieme il servizio migliore per la tua azienda.</p>
              </div>
            </div>

            <div className="mt-6 grid sm:grid-cols-2 gap-4">
              <Field label="Nome azienda" required>
                <input data-testid="demo-company-name" required value={form.company_name} onChange={set("company_name")} className="input-tall w-full" placeholder="Ragione sociale della tua azienda" />
              </Field>
              <Field label="Nome referente" required>
                <input data-testid="demo-contact-name" required value={form.contact_name} onChange={set("contact_name")} className="input-tall w-full" placeholder="Mario Rossi" />
              </Field>
              <Field label="Email" required>
                <input data-testid="demo-email" required type="email" value={form.email} onChange={set("email")} className="input-tall w-full" placeholder="mario@azienda.ch" />
              </Field>
              <Field label="Telefono" required>
                <input data-testid="demo-phone" required value={form.phone} onChange={set("phone")} className="input-tall w-full" placeholder="+41 79 ..." inputMode="tel" />
              </Field>
              <Field label="Settore attività" required>
                <select data-testid="demo-sector" required value={form.business_sector} onChange={set("business_sector")} className="input-tall w-full">
                  <option value="">Seleziona…</option>
                  {SECTORS.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </Field>
              <Field label="Consegne mensili stimate" required>
                <input data-testid="demo-monthly" required type="number" min="0" value={form.monthly_deliveries_estimate} onChange={set("monthly_deliveries_estimate")} className="input-tall w-full" placeholder="50" />
              </Field>
              <Field label="Città / zona operativa">
                <input data-testid="demo-area" value={form.operating_area} onChange={set("operating_area")} className="input-tall w-full" placeholder="Lugano, Mendrisiotto…" />
              </Field>
              <Field label="Piano di interesse">
                <select data-testid="demo-plan" value={form.interested_plan} onChange={set("interested_plan")} className="input-tall w-full">
                  <option value="">Non specificato</option>
                  {PLAN_OPTIONS.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
                </select>
              </Field>
              <div className="sm:col-span-2">
                <Field label="Preferenza contatto">
                  <div className="grid grid-cols-3 gap-2">
                    {CONTACT_METHODS.map((m) => (
                      <button
                        type="button"
                        key={m.id}
                        data-testid={`demo-contact-${m.id}`}
                        onClick={() => setForm((s) => ({ ...s, preferred_contact_method: s.preferred_contact_method === m.id ? "" : m.id }))}
                        className={`h-11 rounded-xl border text-sm font-semibold transition ${
                          form.preferred_contact_method === m.id ? "border-dapnow-blue bg-dapnow-blue-soft text-dapnow-blue" : "border-dapnow-line bg-white text-dapnow-ink hover:border-dapnow-blue/40"
                        }`}
                      >
                        {m.label}
                      </button>
                    ))}
                  </div>
                </Field>
              </div>
              <div className="sm:col-span-2">
                <Field label="Messaggio / note" required>
                  <textarea
                    data-testid="demo-message"
                    required
                    rows={3}
                    value={form.message}
                    onChange={set("message")}
                    className="w-full rounded-xl border border-dapnow-line p-3 text-sm focus:border-dapnow-blue focus:ring-2 focus:ring-dapnow-blue/20 outline-none"
                    placeholder="Raccontaci come potremmo aiutarti: tipologia di consegne, esigenze, volumi attesi…"
                  />
                </Field>
              </div>
            </div>

            <p className="text-xs text-dapnow-muted mt-4">
              Inviando il modulo accetti che DapNow ti contatti per finalità commerciali. I dati non saranno condivisi con terzi.
            </p>

            <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-end">
              <button type="button" onClick={onClose} className="btn-ghost">Annulla</button>
              <button type="submit" data-testid="demo-submit" disabled={submitting} className="btn-primary">
                {submitting ? "Invio…" : <>Invia richiesta <Send className="ml-1.5 h-4 w-4" /></>}
              </button>
            </div>
          </form>
        ) : (
          <div className="text-center py-8" data-testid="demo-success">
            <span className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100 text-emerald-700 mx-auto">
              <CheckCircle2 className="h-8 w-8" />
            </span>
            <h2 className="font-display text-3xl font-extrabold tracking-tighter mt-5">Grazie!</h2>
            <p className="text-dapnow-muted mt-3 max-w-md mx-auto leading-relaxed">
              Abbiamo ricevuto la tua richiesta. Ti contatteremo al più presto per valutare insieme il servizio migliore per la tua azienda.
            </p>
            <button
              data-testid="demo-success-close"
              onClick={onClose}
              className="btn-primary mt-6"
            >
              Chiudi
            </button>
          </div>
        )}
    </MobileModal>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <label className="text-[11px] uppercase tracking-wider text-dapnow-muted font-semibold">
        {label}{required ? <span className="text-dapnow-red ml-0.5">*</span> : null}
      </label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
